import { FC, useEffect, useState } from "react";
import { Input, Tooltip } from "../../../design-system";
import { EditOutlined } from "@ant-design/icons";
import { Link as RouterLink } from "react-router-dom";
import { renameProject } from "../../../common/api/ProjectClient";
import { handleError } from "../../../common/ErrorHandling";
import styles from "./ProjectNameWidget.module.less";

interface ProjectNameWidgetProps {
    projectId: number;
    name: string;
    vendorName?: string;
    supplierId?: number;
    canEdit: boolean;
    onUpdate: () => void;
}

export const ProjectNameWidget: FC<ProjectNameWidgetProps> = ({
    projectId,
    name,
    vendorName,
    supplierId,
    canEdit,
    onUpdate,
}) => {
    const [editing, setEditing] = useState(false);
    const [value, setValue] = useState(name || "");

    useEffect(() => {
        setValue(name || "");
    }, [name]);

    const handleSave = async () => {
        const trimmed = value.trim();
        if (trimmed && trimmed !== name) {
            try {
                await renameProject(projectId, trimmed);
                onUpdate();
            } catch (error) {
                handleError(error);
                setValue(name || "");
            }
        } else {
            setValue(name || "");
        }
        setEditing(false);
    };

    return (
        <div className={`${styles.container} ${editing ? styles.containerEditing : ""}`} onClick={(e) => e.stopPropagation()}>
            {vendorName && (
                supplierId ? (
                    <Tooltip title="Go to buyer brief">
                        <RouterLink
                            to={`/supplier/${supplierId}`}
                            className={styles.vendor}
                            onClick={(e) => e.stopPropagation()}
                        >
                            {vendorName}
                        </RouterLink>
                    </Tooltip>
                ) : (
                    <span className={styles.vendor}>{vendorName}</span>
                )
            )}
            {canEdit && editing ? (
                <Input
                    autoFocus
                    size="small"
                    className={styles.nameInput}
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                    onBlur={handleSave}
                    onPressEnter={handleSave}
                />
            ) : (
                <span
                    className={`${styles.name} ${!canEdit ? styles.nameDisabled : ""}`}
                    onClick={canEdit ? (e) => { e.stopPropagation(); setEditing(true); } : undefined}
                >
                    <span className={styles.nameText}>
                        {name || "Unnamed Project"}
                    </span>
                    {canEdit && <EditOutlined className={styles.editIcon} />}
                </span>
            )}
        </div>
    );
};
